import {
  AdminCreateAiDocumentReviewConfig,
  AdminDeleteAiDocumentReviewConfig,
  AdminFetchAiDocumentReviewConfigs,
  AdminUpdateAiDocumentReviewConfig,
} from 'src/constants/actionTypes'
import type { AiDocumentReviewConfig } from 'src/interfaces/aiDocumentReviewConfig'
import { Action } from 'src/utils/reduxUtils'
import { extractApiErrorMessages } from 'src/utils/errorHandlers'
import { AiDocumentReviewConfigState } from './types'

function resolveApiErrorMessage(error: unknown, fallback: string): string {
  const messages = extractApiErrorMessages(error)

  if (messages.length === 0) {
    return fallback
  }

  return messages.join(', ')
}

const getInitialState = (): AiDocumentReviewConfigState => ({
  partnerId: null,
  configs: [],
  loading: false,
  saving: false,
  error: null,
})

export const aiDocumentReviewConfigReducer = (
  state: AiDocumentReviewConfigState = getInitialState(),
  action: Action<unknown>,
): AiDocumentReviewConfigState => {
  switch (action.type) {
    case AdminFetchAiDocumentReviewConfigs.type.REQUEST: {
      const partnerId = (action.payload as { partnerId: string } | undefined)?.partnerId ?? null

      return {
        ...state,
        partnerId: partnerId ?? state.partnerId,
        loading: true,
        error: null,
      }
    }
    case AdminFetchAiDocumentReviewConfigs.type.SUCCESS:
      return {
        ...state,
        configs: (action.payload as AiDocumentReviewConfig[]) ?? [],
        loading: false,
        error: null,
      }
    case AdminFetchAiDocumentReviewConfigs.type.FAILED:
      return {
        ...state,
        loading: false,
        error: resolveApiErrorMessage(action.payload, 'Failed to load AI review configurations'),
      }

    case AdminCreateAiDocumentReviewConfig.type.REQUEST:
    case AdminUpdateAiDocumentReviewConfig.type.REQUEST:
    case AdminDeleteAiDocumentReviewConfig.type.REQUEST:
      return {
        ...state,
        saving: true,
        error: null,
      }

    case AdminCreateAiDocumentReviewConfig.type.SUCCESS: {
      const created = action.payload as AiDocumentReviewConfig

      return {
        ...state,
        configs: [...state.configs, created],
        saving: false,
        error: null,
      }
    }
    case AdminUpdateAiDocumentReviewConfig.type.SUCCESS: {
      const updated = action.payload as AiDocumentReviewConfig

      return {
        ...state,
        configs: state.configs.map((config) => {
          if (config.id === updated.id) {
            return updated
          }

          return config
        }),
        saving: false,
        error: null,
      }
    }
    case AdminDeleteAiDocumentReviewConfig.type.SUCCESS: {
      const deletedId = (action.payload as { aiDocumentReviewConfigId: string })
        .aiDocumentReviewConfigId

      return {
        ...state,
        configs: state.configs.filter((config) => {
          return config.id !== deletedId
        }),
        saving: false,
        error: null,
      }
    }

    case AdminCreateAiDocumentReviewConfig.type.FAILED:
    case AdminUpdateAiDocumentReviewConfig.type.FAILED:
    case AdminDeleteAiDocumentReviewConfig.type.FAILED:
      return {
        ...state,
        saving: false,
        error: resolveApiErrorMessage(action.payload, 'Failed to save AI review configuration'),
      }

    default:
      return state
  }
}
